import {
  QuickOrder,
  CSVParseErrorCode,
  type CSVParseError,
  QuickOrderValidationError,
} from '@haus-storefront-react/quick-order'
import { quickOrderVariantSkuValidationStrategy } from '@haus-storefront-react/vendure-plugin-configs/products-by-sku'
import { type JSX, useId, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Typography } from '@components'
import { CheckIcon, PlusIcon, TrashIcon } from '@components/icons'
import type { QuickOrderAttributes } from './_propsFn'
import styles from './QuickOrder.module.scss'

type InputMode = 'manual' | 'csv'

type LastResult = {
  successCount: number
  errorCount: number
  errors: string[]
}

function getManualErrorMessage(
  code: string,
  t: (key: string, opts?: Record<string, string | number>) => string,
): string {
  switch (code) {
    case 'SKU_NOT_FOUND':
      return t('quickOrder.manualInvalidSku')
    case 'INSUFFICIENT_STOCK_ERROR':
      return t('quickOrder.manualOutOfStock')
    default:
      return t('general.genericErrorMessage')
  }
}

/**
 * Map CSV parse error codes to translation keys / messages.
 */
function getCSVErrorMessage(
  error: CSVParseError,
  t: (key: string, opts?: Record<string, string>) => string,
): string {
  switch (error.code) {
    case CSVParseErrorCode.INVALID_FORMAT:
      return t('quickOrder.csvInvalidFormat')
    case CSVParseErrorCode.MISSING_SECOND_COLUMN:
      return t('quickOrder.csvMissingQuantity')
    case CSVParseErrorCode.EMPTY_FIRST_COLUMN:
      return t('quickOrder.csvEmptyFirstColumn')
    case CSVParseErrorCode.INVALID_QUANTITY:
      return t('quickOrder.csvInvalidQuantity', { enteredQuantity: error.value ?? '' })
    case CSVParseErrorCode.QUANTITY_ZERO_OR_NEGATIVE:
      return t('quickOrder.csvQuantityZeroOrNegative')
    default:
      return t('quickOrder.csvUnknownError')
  }
}

/**
 * Quick Order widget for Elementor. Supports manual entry and CSV paste to add multiple items to cart.
 * Heading, subtitle and tab labels are configurable via Elementor content controls; styles via style controls.
 */
export const QuickOrderWidget = ({ configStyles }: QuickOrderAttributes): JSX.Element => {
  const { t } = useTranslation()
  const validationStrategy = quickOrderVariantSkuValidationStrategy()
  const idPrefix = useId()
  const [mode, setMode] = useState<InputMode>('manual')
  const [lastResult, setLastResult] = useState<LastResult | null>(null)
  const manualClearRef = useRef<(() => void) | null>(null)

  const submitButtonVariant = configStyles?.submitButtonVariant?.variant ?? 'primary'
  const clearButtonVariant = configStyles?.clearButtonVariant?.variant ?? 'outline'

  const displayTabManual = t('quickOrder.tabManual')
  const displayTabCsv = t('quickOrder.tabCsv')

  const handleSuccess = (result: {
    order?: { lines?: unknown[] }
    errorResults: { message: string }[]
  }) => {
    const successCount = result.order?.lines?.length ?? 0
    const errorCount = result.errorResults.length
    const errors = result.errorResults.map((e) => getManualErrorMessage(e.message, t))

    setLastResult({ successCount, errorCount, errors })

    if (errorCount === 0) {
      manualClearRef.current?.()
    }
  }

  const handleError = (error: Error) => {
    if (error instanceof QuickOrderValidationError) {
      setLastResult({
        successCount: 0,
        errorCount: error.errorResults.length,
        errors: error.errorResults.map((e) => getManualErrorMessage(e.message, t)),
      })
      return
    }

    setLastResult({
      successCount: 0,
      errorCount: 1,
      errors: [error.message],
    })
  }

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const focusTab = (nextMode: InputMode) => {
      const tabId =
        nextMode === 'manual'
          ? `${idPrefix}-quick-order-tab-manual`
          : `${idPrefix}-quick-order-tab-csv`
      const tab = document.getElementById(tabId)
      if (tab instanceof HTMLButtonElement) {
        tab.focus()
      }
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault()
      const nextMode = mode === 'manual' ? 'csv' : 'manual'
      setMode(nextMode)
      focusTab(nextMode)
      return
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      const nextMode = mode === 'csv' ? 'manual' : 'csv'
      setMode(nextMode)
      focusTab(nextMode)
      return
    }

    if (event.key === 'Home') {
      event.preventDefault()
      setMode('manual')
      focusTab('manual')
      return
    }

    if (event.key === 'End') {
      event.preventDefault()
      setMode('csv')
      focusTab('csv')
    }
  }

  return (
    <>
      <div
        className={styles.modeTabs}
        role="tablist"
        aria-label={t('a11y.quickOrder.aria.inputModeTabsLabel')}
      >
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'manual'}
          tabIndex={mode === 'manual' ? 0 : -1}
          aria-controls={`${idPrefix}-quick-order-manual-panel`}
          id={`${idPrefix}-quick-order-tab-manual`}
          className={mode === 'manual' ? styles.modeTabActive : styles.modeTab}
          onClick={() => setMode('manual')}
          onKeyDown={handleTabKeyDown}
        >
          {displayTabManual}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'csv'}
          tabIndex={mode === 'csv' ? 0 : -1}
          aria-controls={`${idPrefix}-quick-order-csv-panel`}
          id={`${idPrefix}-quick-order-tab-csv`}
          className={mode === 'csv' ? styles.modeTabActive : styles.modeTab}
          onClick={() => setMode('csv')}
          onKeyDown={handleTabKeyDown}
        >
          {displayTabCsv}
        </button>
      </div>

      <QuickOrder.Root onSuccess={handleSuccess} onError={handleError}>
        {({ isLoading }) => (
          <div className={styles.form}>
            {lastResult && (
              <div
                className={
                  lastResult.errorCount > 0
                    ? styles.resultMessageError
                    : styles.resultMessageSuccess
                }
                role={lastResult.errorCount > 0 ? 'alert' : 'status'}
                aria-live={lastResult.errorCount > 0 ? 'assertive' : 'polite'}
              >
                {lastResult.errorCount === 0 ? (
                  <div className={styles.successContent}>
                    <CheckIcon width={20} height={20} aria-hidden />
                    <Typography variant="body" as="span">
                      {t('quickOrder.successMessage')}
                    </Typography>
                  </div>
                ) : (
                  <div className={styles.errorContent}>
                    <Typography variant="body" as="span" className={styles.errorSummary}>
                      {t('quickOrder.errorSummary', {
                        failedItemCount: lastResult.errorCount,
                      })}
                    </Typography>
                    <ul className={styles.errorList}>
                      {lastResult.errors.map((err, i) => (
                        <li key={i}>
                          <Typography variant="body" as="span">
                            {err}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div
              id={`${idPrefix}-quick-order-manual-panel`}
              role="tabpanel"
              aria-labelledby={`${idPrefix}-quick-order-tab-manual`}
              className={styles.manualPanel}
              hidden={mode !== 'manual'}
            >
              <QuickOrder.Manual.Root validationStrategy={validationStrategy}>
                {({ rows, getValidRows, clear }) => {
                  manualClearRef.current = () => {
                    clear()
                  }
                  const validCount = getValidRows().length

                  return (
                    <>
                      <div className={styles.formHeader}>
                        <span className={styles.columnHeaderSku}>{t('quickOrder.columnSku')}</span>
                        <span className={styles.columnHeaderQuantity}>
                          {t('quickOrder.columnQuantity')}
                        </span>
                        <span className={styles.columnHeaderActions} aria-hidden />
                      </div>

                      <div className={styles.formRows}>
                        {rows.map((row) => (
                          <QuickOrder.Manual.Row key={row.id} row={row}>
                            <div className={styles.formRow}>
                              <div className={styles.inputCellSku}>
                                <QuickOrder.Manual.Row.IdentifierInput
                                  type="text"
                                  aria-label={t('quickOrder.identifierLabel')}
                                />
                              </div>
                              <div className={styles.inputCellQuantity}>
                                <QuickOrder.Manual.Row.QuantityInput
                                  type="number"
                                  min={1}
                                  aria-label={t('quickOrder.quantityLabel')}
                                />
                              </div>
                              <div className={styles.inputCellActions}>
                                <QuickOrder.Manual.Row.RemoveButton
                                  title={t('quickOrder.removeRow')}
                                  className={styles.removeButton}
                                  aria-label={t('quickOrder.removeRow')}
                                >
                                  <TrashIcon width={18} height={18} aria-hidden />
                                </QuickOrder.Manual.Row.RemoveButton>
                              </div>
                            </div>
                          </QuickOrder.Manual.Row>
                        ))}
                      </div>

                      <QuickOrder.Manual.AddRowButton className={styles.addRowButton}>
                        <PlusIcon width={18} height={18} aria-hidden />
                        {t('quickOrder.addAnotherItem')}
                      </QuickOrder.Manual.AddRowButton>

                      <div className={styles.formActions}>
                        <QuickOrder.Manual.SubmitButton asChild>
                          <Button variant={submitButtonVariant} className={styles.submitButton}>
                            {isLoading ? (
                              <>
                                <span className={styles.spinner} aria-hidden />
                                {t('quickOrder.addingToCart')}
                              </>
                            ) : (
                              t('quickOrder.addItemsToCart', {
                                variantCount: validCount,
                              })
                            )}
                          </Button>
                        </QuickOrder.Manual.SubmitButton>

                        <Button
                          type="button"
                          variant={clearButtonVariant as 'outline'}
                          className={styles.clearButton}
                          onClick={() => {
                            clear()
                            setLastResult(null)
                          }}
                          disabled={isLoading}
                        >
                          {t('quickOrder.clearForm')}
                        </Button>
                      </div>
                    </>
                  )
                }}
              </QuickOrder.Manual.Root>
            </div>

            <div
              id={`${idPrefix}-quick-order-csv-panel`}
              role="tabpanel"
              aria-labelledby={`${idPrefix}-quick-order-tab-csv`}
              className={styles.csvPanel}
              hidden={mode !== 'csv'}
            >
              <QuickOrder.CSV.Root validationStrategy={validationStrategy}>
                {({ value, setValue, rows, errors, hasErrors, hasRows, clear }) => (
                  <div className={styles.csvMode}>
                    <div className={styles.csvInstructions}>
                      <Typography variant="body" as="p">
                        {t('quickOrder.csvInstructions')}
                      </Typography>
                      <code className={styles.code}>{t('quickOrder.csvFormatComma')}</code>
                      <Typography variant="body" as="span">
                        {' '}
                        {t('quickOrder.csvOr')}{' '}
                      </Typography>
                      <code className={styles.code}>{t('quickOrder.csvFormatSemicolon')}</code>
                      <Typography variant="body" as="span">
                        {' '}
                        {t('quickOrder.csvOr')}{' '}
                      </Typography>
                      <code className={styles.code}>{t('quickOrder.csvFormatTab')}</code>
                    </div>

                    <textarea
                      className={styles.csvTextarea}
                      rows={10}
                      disabled={isLoading}
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      aria-label={t('a11y.quickOrder.aria.csvTextareaLabel')}
                    />

                    {hasErrors && (
                      <div className={styles.csvErrors} role="alert">
                        <Typography variant="body" as="span" className={styles.csvErrorsTitle}>
                          {t('quickOrder.parseErrors')}
                        </Typography>
                        <ul className={styles.csvErrorList}>
                          {errors.map((err, i) => (
                            <li key={i}>
                              {t('quickOrder.lineLabel', { lineNumber: err.line })}:{' '}
                              {getCSVErrorMessage(err, t)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {hasRows && !hasErrors && (
                      <div className={styles.csvPreview}>
                        <Typography variant="body" as="span" className={styles.csvPreviewTitle}>
                          {t('quickOrder.itemsToAdd', {
                            itemCountToAdd: rows.length,
                          })}
                        </Typography>
                        <ul className={styles.csvPreviewList}>
                          {rows.slice(0, 5).map((row, i) => (
                            <li key={i}>
                              {row.identifier} × {row.quantity}
                            </li>
                          ))}
                          {rows.length > 5 && (
                            <li>
                              {t('quickOrder.andMore', {
                                additionalItemCount: rows.length - 5,
                              })}
                            </li>
                          )}
                        </ul>
                      </div>
                    )}

                    <div className={styles.formActions}>
                      <QuickOrder.CSV.SubmitButton asChild>
                        <Button variant={submitButtonVariant} className={styles.submitButton}>
                          {isLoading ? (
                            <>
                              <span className={styles.spinner} aria-hidden />
                              {t('quickOrder.addingToCart')}
                            </>
                          ) : (
                            t('quickOrder.addItemsToCart', {
                              variantCount: rows.length,
                            })
                          )}
                        </Button>
                      </QuickOrder.CSV.SubmitButton>

                      <Button
                        type="button"
                        variant={clearButtonVariant as 'outline'}
                        className={styles.clearButton}
                        onClick={clear}
                        disabled={!value}
                      >
                        {t('quickOrder.clear')}
                      </Button>
                    </div>
                  </div>
                )}
              </QuickOrder.CSV.Root>
            </div>
          </div>
        )}
      </QuickOrder.Root>
    </>
  )
}
